import React, { useMemo } from "react";
import Label from "../../../../../common/components/Label";
import StackWrapper from "../../StackWrapper";
import { FormControl, Typography } from "@mui/material";
import { IExperienceRange } from "../../../../../types/campaign.types";
import SelectWrapper from "../../SelectWrapper";
import { COLOR_MAP } from "../../../../../constants/ColorNames";
import { getIsFresher } from "../../../../../utils/whatsAppUtils";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import CommonTooltip from "../../../../../common/components/CommonTooltip";
import { useLocation } from "react-router-dom";

interface IProps {
  experienceRange: IExperienceRange;
  handleUpdateExperienceRange: (newRange: IExperienceRange) => void;
  error: string;
}

const ExperienceRange: React.FC<IProps> = ({
  experienceRange,
  handleUpdateExperienceRange,
  error,
}) => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location?.search);
    const { minExp, maxExp } = useSelector(
    (state: RootState) => state.campaign.disabledFields
  );
  let triggeredFromPage = queryParams.get("triggeredFromPage")
   const isMinExpLocked =
    triggeredFromPage === 'SRP' && minExp
     const isMaxExpLocked =
    triggeredFromPage === 'SRP' && maxExp
  const { minExperienceRangeOptions, maxExperienceRangeOptions } =
    useMemo(() => {
      const EXPERIENCE_OPTIONS = Array(51)
        .fill("")
        .map((_, index) => {
          const labelString = `${index} ${index < 2 ? "year" : "years"}`;

          return {
            label: labelString,
            value: labelString,
          };
        });
      return {
        minExperienceRangeOptions: [
          { label: "Fresher", value: "Fresher" },
          ...EXPERIENCE_OPTIONS,
        ],
        // slice is used to remove 0 year from max experience options
        maxExperienceRangeOptions: EXPERIENCE_OPTIONS.slice(1),
      };
    }, []);

  const handleChangeMinExperience = (newValue: string) => {
    const newRange: IExperienceRange = {
      min: newValue || "",
      max: getIsFresher(newValue) ? "" : experienceRange.max,
    };
    handleUpdateExperienceRange(newRange);
  };

  const handleChangeMaxExperience = (newValue: string) => {
    const newRange: IExperienceRange = {
      min: experienceRange.min,
      max: newValue || "",
    };
    handleUpdateExperienceRange(newRange);
  };

  return (
    <StackWrapper>
      <Label
        colorType="light"
        label="Experience range"
        required
        labelFor="experienceRange"
      />

      <FormControl
        fullWidth
        sx={{
          display: "flex",
          flexDirection: "row",
          border: `1px solid ${COLOR_MAP.BORDER_GREY}`,
          borderRadius: "4px",
          ...(!!error && (!experienceRange.max || !experienceRange.min)
            ? {
                borderColor: COLOR_MAP.ERROR,
              }
            : {}),
        }}
      >
          <CommonTooltip
                            width='270px'
                            title={isMinExpLocked ?  'Search filters cannot be edited during campaign setup.' : ''} 
                            customZIndex={99999999}
                            offset={[40,10]}
                            // arrowShift={'35px'}
                            >
        <div style={{width:'100%'}}>
        <SelectWrapper
          handleChange={handleChangeMinExperience}
          placeholder="Min"
          isDisabled={isMinExpLocked}
          menuItems={
            experienceRange?.max
              ? minExperienceRangeOptions?.filter(
                  (item) =>
                    +(item.value?.split(" ")?.[0] || 0) <=
                    +(experienceRange.max?.split(" ")?.[0] || 0) || getIsFresher(item.value)
                )
              : minExperienceRangeOptions
          }
          value={experienceRange.min || "none"}
          renderValue={(value) =>
            value === "none" ? (
              <span style={{ color: COLOR_MAP.GREY_4 }}>Min</span>
            ) : (
              value
            )
          }
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
              borderRight: `1px solid ${COLOR_MAP.BORDER_GREY}`,
              borderBottomRightRadius: 0,
              borderTopRightRadius: 0,
            },
          }}
        />
        </div>
        </CommonTooltip>
        <CommonTooltip
                            width='270px'
                            title={isMaxExpLocked ?  'Search filters cannot be edited during campaign setup.' : ''} 
                            customZIndex={99999999}
                            offset={[40,10]}
                            // arrowShift={'35px'}
                            >
        <div style={{width:'100%'}}>
        <SelectWrapper
          handleChange={handleChangeMaxExperience}
          placeholder="Max"
          isDisabled={isMaxExpLocked}
          menuItems={
            experienceRange?.min
              ? maxExperienceRangeOptions?.filter(
                  (item) =>
                    +(item.value?.split(" ")?.[0] || 0) >=
                    +(experienceRange.min?.split(" ")?.[0] || 0)
                )
              : maxExperienceRangeOptions
          }
          renderValue={(value) =>
            value === "none" ? (
              <span style={{ color: COLOR_MAP.GREY_4 }}>Max</span>
            ) : (
              value
            )
          }
          value={experienceRange.max || "none"}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
          showDisabled={getIsFresher(experienceRange.min)}
          showDisabledMsg={getIsFresher(experienceRange.min)}
          disabledMsg="Max Experience cannot be selected for Freshers."
        />
        </div>
        </CommonTooltip>
      </FormControl>
      {!!error && (!experienceRange.max || !experienceRange.min) && (
        <Typography sx={{ color: COLOR_MAP.ERROR, fontSize: 12 }}>
          {error}
        </Typography>
      )}
    </StackWrapper>
  );
};

export default React.memo(ExperienceRange);
